import { useEffect, useState } from 'react';
import { FloridaJourney } from './components/FloridaJourney';
import { JeffAvatar } from './components/JeffAvatar';
import { EventPanel } from './components/EventPanel';
import { ContactPanel } from './components/ContactPanel';
import { CitrusSlice } from './components/CitrusSlice';
import { RsvpFlow } from './components/RsvpFlow';
import { SubmittedRsvpPanel } from './components/SubmittedRsvpPanel';
import { AdminApp } from './pages/AdminApp';
import { PrintableInvite } from './pages/PrintableInvite';
import { api, EventDetails, PublicInvite, RequestError } from './lib/api';

function tokenFromPath() { const match = location.pathname.match(/^\/r\/([A-Za-z0-9_-]{9,16})\/?$/); return match?.[1] || null; }

export function App() {
  const printMatch = location.pathname.match(/^\/admin\/print\/(\d+)\/?$/);
  if (printMatch) return <PrintableInvite invitationId={Number(printMatch[1])} />;
  if (location.pathname.startsWith('/admin')) return <AdminApp />;
  return <PublicApp />;
}

function PublicApp() {
  const token = tokenFromPath();
  const [invite, setInvite] = useState<PublicInvite | null>(null);
  const [details, setDetails] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [invalid, setInvalid] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [eventOpen, setEventOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [submittedOpen, setSubmittedOpen] = useState(false);
  const [journeyYear, setJourneyYear] = useState(1956);
  const [confirmation, setConfirmation] = useState<{ status: 'yes' | 'no'; count: number } | null>(null);
  useEffect(() => {
    if (!token) return;
    let active = true;
    api<{ invite: PublicInvite; event: EventDetails }>(`/api/invite/${token}`).then((body) => {
      if (!active) return;
      setInvite(body.invite);
      setDetails(body.event);
      setSubmittedOpen(body.invite.state === 'submitted');
    }).catch((error) => {
      if (!active) return;
      if (error instanceof RequestError && error.status === 404) setInvalid(true);
      else setLoadError(true);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, loadAttempt]);
  const reaction = confirmation?.status === 'yes' ? 'yes' : confirmation?.status === 'no' ? 'no' : rsvpOpen ? 'rsvp' : 'idle';
  return (
    <main className="public-shell">
      <header className="topbar">
        {details ? <button className="text-button" onClick={() => setEventOpen(true)}><i className="fa-regular fa-calendar-days" aria-hidden="true" /> Event details</button> : <button className="text-button" onClick={() => setContactOpen(true)}><i className="fa-regular fa-envelope" aria-hidden="true" /> Missing the invite?</button>}
      </header>
      <div className="grain" aria-hidden="true" />
      <CitrusSlice />
      <section className="hero">
        <div className="hero-copy">
          <p className="era-counter" aria-live="off">
            <span>Est. 1956 - {journeyYear}</span>
            <small>Still Florida</small>
          </p>
          <h1><span>Jeff's</span><strong>70</strong><em>TH</em></h1>
          <p className="hero-subtitle"><span>A birthday salute</span><small>to the original</small><strong>Florida Man.</strong></p>
          <div className="date-lockup"><span>NOV 8</span><i /> <span>EUSTIS, FL</span></div>
          {loading && <div className="invite-status" role="status">Dusting off your invitation…</div>}
          {invalid && <div className="invite-status error-state"><h2>Well, hell.</h2><p>This invitation link doesn't seem to work.</p><small>If you think that's a mistake, use the organizer form under Event details.</small></div>}
          {loadError && <div className="invite-status error-state" role="alert"><h2>Couldn't load the invitation.</h2><p>Your link may be fine; the site could not reach the guest list.</p><button className="inline-contact-link" onClick={() => { setLoading(true); setInvalid(false); setLoadError(false); setLoadAttempt((attempt) => attempt + 1); }}>Try again</button></div>}
          {!loading && !invalid && !loadError && invite?.state === 'open' && <button className="button primary rsvp-cta" onClick={() => setRsvpOpen(true)}>RSVP <i className="fa-solid fa-arrow-right" aria-hidden="true" /></button>}
          {!loading && !token && <div className="invite-status missing-invite-status"><p>Open the personal invitation link from your text or email to RSVP.</p><p className="contact-invite-note">Contact the host if you're <button className="inline-contact-link" onClick={() => setContactOpen(true)}>missing an invite.</button></p></div>}
          {invite?.state === 'submitted' && !confirmation && <button className="button secondary view-rsvp" onClick={() => setSubmittedOpen(true)}><i className="fa-regular fa-eye" aria-hidden="true" /> View RSVP</button>}
          {confirmation && <div className={`confirmation ${confirmation.status}`}>
            <div className="orange-burst" aria-hidden="true">✦</div>
            <h2>{confirmation.status === 'yes' ? "You're officially part of the legend." : "Thanks for letting us know. We'll miss you."}</h2>
            {confirmation.status === 'yes' && <p>{confirmation.count} {confirmation.count === 1 ? 'person' : 'people'} confirmed.</p>}
          </div>}
        </div>
        <FloridaJourney onYearChange={setJourneyYear} />
      </section>
      <JeffAvatar reaction={reaction} />
      <footer><span>70 years</span><i className="footer-separator" aria-hidden="true">•</i><span>7 Florida stops</span><i className="footer-separator" aria-hidden="true">•</i><span>1 questionable legend</span></footer>
      <EventPanel open={Boolean(details) && eventOpen} event={details} onClose={() => setEventOpen(false)} />
      <ContactPanel open={contactOpen} onClose={() => setContactOpen(false)} />
      {invite?.state === 'submitted' && <SubmittedRsvpPanel open={submittedOpen} invite={invite} onClose={() => setSubmittedOpen(false)} />}
      {rsvpOpen && invite?.state === 'open' && token && details && <RsvpFlow invite={invite} event={details} token={token} onCancel={() => setRsvpOpen(false)} onShowDetails={() => { setRsvpOpen(false); setEventOpen(true); }} onDone={(status, count) => { setRsvpOpen(false); setConfirmation({ status, count }); }} />}
    </main>
  );
}
